<template>
    <div
        ref="root"
        v-bind="oneAtTime && accordionId ? { 'id': accordionId } : {}"
        class="accordion"
        :class="{ 'accordion-disable-expanded-borders': disableExpandedBorders }"
    >
        <div
            v-for="(card, index) in cards"
            :key="index"
            :class="classes"
        >
            <component :is="elementType" class="card-header">
                <a
                    v-if="isLinkTitle"
                    :id="card.headingId"
                    role="button"
                    :href="'#' + card.collapseId"
                    data-bs-toggle="collapse"
                    :data-bs-target="'#' + card.collapseId"
                    :aria-controls="'#' + card.collapseId"
                    :aria-expanded="card.expanded"
                >
                    <span class="accordion-card-title">
                        <span class="accordion-title">{{ card.title }}</span>
                        <span class="accordion-icon" aria-hidden="true" />
                    </span>
                </a>
                <button
                    v-else
                    :id="card.headingId"
                    :class="card.expanded ? 'show' : 'collapsed'"
                    data-bs-toggle="collapse"
                    :data-bs-target="'#' + card.collapseId"
                    :aria-controls="'#' + card.collapseId"
                    :aria-expanded="card.expanded"
                    type="button"
                    v-bind="isDisabled ? { 'disabled': '' } : {}"
                >
                    <span class="accordion-card-title" :class="isLarge ? 'ob1-accordion-large' : ''">
                        <span v-if="card.icon" :class="'item-icon ' + card.icon" aria-hidden="true" />
                        <span class="accordion-title">{{ card.title }}</span>
                        <span class="accordion-icon" aria-hidden="true" />
                    </span>
                </button>
            </component>
            <section
                :id="card.collapseId"
                class="collapse"
                :class="card.expanded ? 'show' : ''"
                :aria-labelledby="card.headingId"
                v-bind="oneAtTime && accordionId ? { 'data-bs-parent': '#' + accordionId } : {}"
            >
                <slot :name="card.headingId" class="accordion-card-body" :class="isLarge ? 'lead' : 'standard'" />
            </section>
        </div>
    </div>
</template>

<script lang="ts">
import {defineComponent, type PropType} from 'vue'
import {Card} from "./models/Card";

export default defineComponent({
    name: "Accordion",
    props: {
        // Permet de paramétrer le type d'un élement html, type h3 ou autre
        elementType: {
            type: String,
            default: 'div'
        },
        cards: {
            type: Array as PropType<Array<Card>>,
            required: true
        },
        isLinkTitle: {
            type: Boolean,
            required: false,
            default: false
        },
        accordionId: {
            type: String,
            required: false,
            default: null
        },
        oneAtTime: {
            type: Boolean,
            required: false,
            default: false
        },
        isDisabled: {
            type: Boolean,
            required: false,
            default: false
        },
        isLarge: {
            type: Boolean,
            required: false,
            default: false
        },
        disableExpandedBorders: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    computed: {
        classes() {
            let classes = ['card']
            if (!this.oneAtTime) {
                classes.push('multi')
            }
            if (this.isDisabled) {
                classes.push('disabledCard')
            }
            return classes
        }
    },
    mounted() {
        const root = this.$refs.root as HTMLElement;
        root.querySelectorAll('.collapse').forEach((el) => {
            el.addEventListener('shown.bs.collapse', (event: any) => {
                this.onCollapseChange(event.target.id, true)
            });
            el.addEventListener('hidden.bs.collapse', (event: any) => {
                this.onCollapseChange(event.target.id, false)
            });
        });
    },
    methods: {
        onCollapseChange(collapseId: string, isExpanded: boolean) {
            const card = this.cards.find(c => c.collapseId === collapseId);
            if (!card) return;
            this.$emit('on-toggle', card, isExpanded);
        }
    }
});
</script>

<style lang="scss">
.accordion.accordion-disable-expanded-borders {
    .card-header a,
    .card-header button {
        border-top: unset;
    }

    .card:last-child {
        .card-header a,
        .card-header button {
            border-bottom: unset;
        }
    }

    .card {
        .card-header:has([aria-expanded=true]) a,
        .card-header:has([aria-expanded=true]) button,
        &:has(.card-header [aria-expanded=true]) + .card .card-header a,
        &:has(.card-header [aria-expanded=true]) + .card .card-header button {
            border-top-color: unset;
        }

        &:last-child:has(.card-header [aria-expanded=true]) {
            border-bottom: unset;
        }
    }
}
</style>
